import type { RecipePayload } from '@ctcells/contract'
import { t, type MessageKey } from '@ctcells/i18n'

import { STEP_SEQUENCE_OPTIONS, RETIRED_SEQUENCE_NAMES } from './recipeVocabulary.ts'

/**
 * 「새 레시피 › 값 직접 설정」의 초안 — **서버가 받는 본문**(`recipe.schema.json`)을 칸으로 쪼갠 것.
 *
 * 🔴 **칸의 출처는 스키마다**(`ctcells-core/app/resources/schema/recipe.schema.json` v0.9.1 · 2026-09-29 대조).
 *    와이어프레임의 칸을 옮기지 않았다. 필수·범위·열거는 그 파일의 `required`·`minimum`·`maximum`·`enum` 을
 *    그대로 옮겨 적은 것이고, 판정의 권위는 여전히 서버(`POST /recipes` → `RECIPE_VALIDATION_FAIL`)다.
 *    여기서 막는 것은 「보내 봐야 반드시 떨어지는 것」(필수 칸이 빔 · 숫자가 아님 · 범위 밖)뿐이다.
 * 🔴 **기본값을 지어내지 않는다.** 스키마의 `default` 는 전 파일에 하나(`SampleGroup.role`)이고 그 칸은
 *    JSON 절 안에 있다 ⇒ `emptyDraft()` 의 칸은 전부 **빈 채로**다. 빈 칸은 본문에서 «빠진다»(0 이 아니다).
 *    ⚠ 화면의 «시작값»은 빈 초안이 아니라 실물 표준 본문이다(eric 2026-09-30 · `recipeDefaults.ts`) —
 *    지어낸 값이 아니라 실서버 레시피 전수의 최빈 본문이다. 「비우기」가 이 빈 초안으로 간다.
 * 🔴 **불러온 본문 위에 고친 칸만 얹는다**(`CLAUDE.md` 「PUT 은 받은 것 위에」와 같은 규율).
 *    칸마다 `base`(받은 원본)를 지니고, 폼이 아는 칸만 글자에서 다시 짓는다 — 폼이 모르는 키는 그대로 간다.
 *    비운 칸은 «지운다» — 건너뛰면 옛 값이 되살아나 나간다.
 */

/**
 * 편집기가 짓는 본문의 판 — 스키마 `properties.schema_version.const` 원문. 미확정 값이 아니라 **계약 상수**다
 * (그 외의 값은 스키마가 거부한다). 봉투(`RecipeWriteEnvelope.schema_version`)가 실어 간다 — 서버가 필수로 받는다.
 */
export const RECIPE_SCHEMA_VERSION = 'rcp/1.0'

// ── 칸 정의 ────────────────────────────────────────────────────────────────

export type FieldKind = 'int' | 'number' | 'text' | 'enum' | 'bool' | 'list' | 'json'

export interface FieldSpec {
  /** 점 경로 — `source.slot` 처럼 한 단 아래 객체를 가리킬 수 있다 */
  path: string
  label: MessageKey
  kind: FieldKind
  min?: number
  max?: number
  /** `enum` 의 값 목록 — 스키마 `enum` 원문 */
  options?: readonly string[]
  /** `list` 가 같은 값을 두 번 받지 않는다(스키마 `uniqueItems`) */
  unique?: boolean
  /** `json` 이 담아야 할 꼴 */
  shape?: 'object' | 'array'
}

/** `StainingStep.operation` — 스키마 `enum` 원문 */
export const STAINING_OPERATIONS = [
  'dispense',
  'aspirate',
  'wash',
  'incubate',
  'mix',
  'shake',
] as const
/** `TipPolicy` — 스키마 `enum` 원문. 🔴 한글로 덮지 않는다(`per_sample` 은 도메인 규칙이다) */
export const TIP_POLICIES = [
  'once',
  'always',
  'per_source',
  'per_destination',
  'never',
  'per_sample',
] as const
const WASTE_STREAMS = ['general', 'biohazard'] as const
const BODY_POSITIONS = ['before', 'after'] as const

/** `CentrifugeParams` — 필수 `speed_rpm`·`duration_sec` */
export const CENTRIFUGE_REQUIRED: readonly FieldSpec[] = [
  { path: 'speed_rpm', label: 'recipeEdit.f.speedRpm', kind: 'int', min: 0 },
  { path: 'duration_sec', label: 'recipeEdit.f.durationSec', kind: 'int', min: 0 },
]
export const CENTRIFUGE_OPTIONAL: readonly FieldSpec[] = [
  { path: 'temperature_celsius', label: 'recipeEdit.f.temperature', kind: 'number' },
]

/** `AssemblerParams` — 필수 `uv_duration_sec`·`drain_angle_deg` */
export const ASSEMBLER_REQUIRED: readonly FieldSpec[] = [
  { path: 'uv_duration_sec', label: 'recipeEdit.f.uvDurationSec', kind: 'int', min: 0 },
  { path: 'drain_angle_deg', label: 'recipeEdit.f.drainAngle', kind: 'int', min: 0, max: 360 },
]
export const ASSEMBLER_OPTIONAL: readonly FieldSpec[] = [
  { path: 'uv_intensity', label: 'recipeEdit.f.uvIntensity', kind: 'number', min: 0 },
  { path: 'uv_tilt_angle_deg', label: 'recipeEdit.f.uvTilt', kind: 'int', min: 0, max: 360 },
  { path: 'load_tilt_angle_deg', label: 'recipeEdit.f.loadTilt', kind: 'int', min: 0, max: 360 },
  {
    path: 'z_stage.descend_speed_pct',
    label: 'recipeEdit.f.zDescend',
    kind: 'int',
    min: 1,
    max: 100,
  },
  { path: 'z_stage.hold_sec', label: 'recipeEdit.f.zHold', kind: 'int', min: 0 },
]

/** `StainingParams` 의 스텝 밖 칸 — 전부 선택. 필수는 `steps`(1개 이상) 하나다 */
export const STAINING_OPTIONAL: readonly FieldSpec[] = [
  {
    path: 'default_tip_policy',
    label: 'recipeEdit.f.defaultTip',
    kind: 'enum',
    options: TIP_POLICIES,
  },
  { path: 'default_liquid_class', label: 'recipeEdit.f.defaultLiquid', kind: 'text' },
  { path: 'reagent_storage_celsius', label: 'recipeEdit.f.storageTemp', kind: 'number' },
]

/** `constraints` — 칸이 하나다(`additionalProperties: false`). `null` 은 「미설정」이라 비움과 같다 */
export const CONSTRAINT_FIELDS: readonly FieldSpec[] = [
  { path: 'max_allowed_delay_sec', label: 'recipeEdit.f.maxDelay', kind: 'int', min: 0 },
]

const LOCATION_LABELS = {
  source: {
    kind: 'recipeEdit.f.sourceKind',
    slot: 'recipeEdit.f.sourceSlot',
    point: 'recipeEdit.f.sourcePoint',
    waste: 'recipeEdit.f.sourceWaste',
  },
  target: {
    kind: 'recipeEdit.f.targetKind',
    slot: 'recipeEdit.f.targetSlot',
    point: 'recipeEdit.f.targetPoint',
    waste: 'recipeEdit.f.targetWaste',
  },
} as const satisfies Record<string, Record<string, MessageKey>>

/** `ModuleLocation` — 네 칸 전부 선택. 다만 `anyOf(location_kind | point_id)` 가 걸린다 */
const locationFields = (side: 'source' | 'target'): FieldSpec[] => [
  { path: `${side}.location_kind`, label: LOCATION_LABELS[side].kind, kind: 'text' },
  { path: `${side}.slot`, label: LOCATION_LABELS[side].slot, kind: 'int', min: 1 },
  { path: `${side}.point_id`, label: LOCATION_LABELS[side].point, kind: 'text' },
  { path: `${side}.waste_container_id`, label: LOCATION_LABELS[side].waste, kind: 'text' },
]

/** `ModuleStep` — 필수 `step_id`·`sequence`. `sequence` 목록은 절마다 다르다(`moduleStepRequired`) */
export const MODULE_STEP_OPTIONAL: readonly FieldSpec[] = [
  { path: 'round', label: 'recipeEdit.f.round', kind: 'int', min: 1 },
  { path: 'chambers', label: 'recipeEdit.f.chambers', kind: 'list', unique: true },
  { path: 'payload_kind', label: 'recipeEdit.f.payloadKind', kind: 'text' },
  ...locationFields('source'),
  ...locationFields('target'),
  { path: 'point_id', label: 'recipeEdit.f.pointId', kind: 'text' },
  {
    path: 'body_position',
    label: 'recipeEdit.f.bodyPosition',
    kind: 'enum',
    options: BODY_POSITIONS,
  },
  { path: 'note', label: 'recipeEdit.f.note', kind: 'text' },
  /*
   * 🔴 **`params` 는 JSON 이다.** 허용 키·범위의 권위가 시퀀스마다 다른 스키마
   *    (코어 `specs/sequences/<모듈>.<시퀀스>.schema.json`)이고 그 파일이 이 리포에 없다 —
   *    칸을 지어내지 않고 객체를 그대로 받는다. 서버가 커밋 때 그 스키마로 잰다(`_sequence_param_issues`).
   */
  { path: 'params', label: 'recipeEdit.f.params', kind: 'json', shape: 'object' },
]

export type StepSection = 'centrifuge' | 'assembler' | 'staining'

export function moduleStepRequired(section: StepSection): readonly FieldSpec[] {
  return [
    { path: 'step_id', label: 'recipeEdit.f.stepId', kind: 'text' },
    {
      path: 'sequence',
      label: 'recipeEdit.f.sequence',
      kind: 'enum',
      options: STEP_SEQUENCE_OPTIONS[section],
    },
  ]
}

/** `StainingStep` — 필수 `step_id`·`operation`·`target_wells` */
export const STAINING_STEP_REQUIRED: readonly FieldSpec[] = [
  { path: 'step_id', label: 'recipeEdit.f.stepId', kind: 'text' },
  {
    path: 'operation',
    label: 'recipeEdit.f.operation',
    kind: 'enum',
    options: STAINING_OPERATIONS,
  },
  { path: 'target_wells', label: 'recipeEdit.f.targetWells', kind: 'list', unique: true },
]
export const STAINING_STEP_OPTIONAL: readonly FieldSpec[] = [
  { path: 'reagent_channel', label: 'recipeEdit.f.reagentChannel', kind: 'int', min: 1 },
  { path: 'volume_ul', label: 'recipeEdit.f.volumeUl', kind: 'number', min: 0 },
  { path: 'wait_sec', label: 'recipeEdit.f.waitSec', kind: 'number', min: 0 },
  { path: 'shake.rpm', label: 'recipeEdit.f.shakeRpm', kind: 'int', min: 0 },
  { path: 'shake.duration_sec', label: 'recipeEdit.f.shakeSec', kind: 'int', min: 0 },
  { path: 'mix.repetitions', label: 'recipeEdit.f.mixRepetitions', kind: 'int', min: 1 },
  { path: 'mix.volume_ul', label: 'recipeEdit.f.mixVolume', kind: 'number', min: 0 },
  { path: 'repetitions', label: 'recipeEdit.f.repetitions', kind: 'int', min: 1 },
  { path: 'temperature_celsius', label: 'recipeEdit.f.temperature', kind: 'number' },
  { path: 'tip_policy', label: 'recipeEdit.f.tipPolicy', kind: 'enum', options: TIP_POLICIES },
  { path: 'liquid_class', label: 'recipeEdit.f.liquidClass', kind: 'text' },
  { path: 'flow_rate_ul_s', label: 'recipeEdit.f.flowRate', kind: 'number', min: 0 },
  { path: 'to_waste', label: 'recipeEdit.f.toWaste', kind: 'bool' },
  { path: 'waste_stream', label: 'recipeEdit.f.wasteStream', kind: 'enum', options: WASTE_STREAMS },
  { path: 'touch_tip', label: 'recipeEdit.f.touchTip', kind: 'bool' },
  { path: 'air_gap_ul', label: 'recipeEdit.f.airGap', kind: 'number', min: 0 },
  { path: 'push_out_ul', label: 'recipeEdit.f.pushOut', kind: 'number', min: 0 },
  { path: 'max_step_duration_sec', label: 'recipeEdit.f.maxStepSec', kind: 'int', min: 0 },
]

/**
 * 염색 스텝의 조건부 필수 — 스키마 `StainingStep.allOf` 의 `if operation … then required` 를 옮겼다.
 * `aspirate` 는 `to_waste: true`(전량 제거)면 부피를 빼도 된다(RCP-STEP-04).
 */
export function stainingConditionalRequired(operation: string, toWaste: string): string[] {
  switch (operation) {
    case 'dispense':
    case 'wash':
      return ['reagent_channel', 'volume_ul']
    case 'aspirate':
      return toWaste === 'true' ? [] : ['volume_ul']
    case 'incubate':
      return ['wait_sec']
    case 'shake':
      return ['shake.duration_sec']
    case 'mix':
      return ['mix.repetitions', 'mix.volume_ul']
    default:
      return []
  }
}

/**
 * 동작에 따라 **스키마가 금지하는** 칸 — `allOf` 의 `then: not required` 두 줄.
 * ⓐ `temperature_celsius` 는 `dispense`·`aspirate`·`mix` 에 못 쓴다(RCP-VAL-09)
 * ⓑ `shake` 는 `operation: shake` 에서만 쓴다(RCP-VAL-07)
 * 🔴 이 칸들은 그 동작에서 **보이지 않고 본문에서도 빠진다** — 적어 보내면 반드시 422 다.
 *    동작을 모를 때(빈 칸)는 막지 않는다.
 */
export function stainingForbidden(operation: string): string[] {
  const out: string[] = []
  if (['dispense', 'aspirate', 'mix'].includes(operation)) out.push('temperature_celsius')
  if (['dispense', 'aspirate', 'mix', 'incubate', 'wash'].includes(operation)) {
    out.push('shake.rpm', 'shake.duration_sec')
  }
  return out
}

/** 폼이 아는 칸 전부(스텝 · 절 칸) — 「스키마에 없는 칸」을 세는 데 쓴다 */
function knownKeys(specs: readonly FieldSpec[]): Set<string> {
  return new Set(specs.map((s) => s.path.split('.')[0] ?? s.path))
}

// ── 초안 모양 ──────────────────────────────────────────────────────────────

export interface DraftFields {
  /** 받은 원본. 폼이 모르는 키를 지닌다 — 새로 지을 때는 `{}` */
  base: Record<string, unknown>
  /** 칸의 입력 글자(경로 → 글자). 🔴 **숫자로 미리 바꾸지 않는다** — 치는 중인 `1.` 이 사라진다 */
  text: Record<string, string>
}

export interface DraftStep extends DraftFields {
  /** 화면 key. 본문에 실리지 않는다 */
  key: string
}

/** 최상위에서 JSON 으로 받는 절 — 폼으로 옮기지 않은 것들 */
export const JSON_SECTIONS = [
  { key: 'reagents', label: 'recipeEdit.json.reagents', shape: 'object' },
  { key: 'labware', label: 'recipeEdit.json.labware', shape: 'array' },
  { key: 'tray_path', label: 'recipeEdit.json.trayPath', shape: 'object' },
  { key: 'liquid_classes', label: 'recipeEdit.json.liquidClasses', shape: 'object' },
  { key: 'runtime_parameters', label: 'recipeEdit.json.runtimeParameters', shape: 'array' },
] as const satisfies readonly { key: string; label: MessageKey; shape: 'object' | 'array' }[]

export type JsonSectionKey = (typeof JSON_SECTIONS)[number]['key']

export interface RecipeDraft {
  centrifuge: DraftFields & { steps: DraftStep[] }
  assembler: DraftFields & { steps: DraftStep[] }
  staining: DraftFields & { steps: DraftStep[]; moduleSteps: DraftStep[] }
  constraints: DraftFields
  json: Record<JsonSectionKey, string>
  /** 최상위의 그 밖의 키(불러온 본문에서 온 것). 🔴 버리지 않는다 — 판정은 서버가 한다 */
  rest: Record<string, unknown>
}

let stepSeq = 0
/** 스텝 줄의 화면 key — 순서 바꾸기·삭제에도 입력 상태가 엉키지 않게 */
export function newStepKey(): string {
  stepSeq += 1
  return `step-${String(stepSeq)}`
}

const emptyFields = (): DraftFields => ({ base: {}, text: {} })

export function emptyDraft(): RecipeDraft {
  return {
    centrifuge: { ...emptyFields(), steps: [] },
    assembler: { ...emptyFields(), steps: [] },
    staining: { ...emptyFields(), steps: [], moduleSteps: [] },
    constraints: emptyFields(),
    json: { reagents: '', labware: '', tray_path: '', liquid_classes: '', runtime_parameters: '' },
    rest: {},
  }
}

export function emptyStep(): DraftStep {
  return { key: newStepKey(), base: {}, text: {} }
}

// ── 값 ↔ 글자 ──────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getPath(obj: Record<string, unknown>, path: string): unknown {
  const [head, tail] = path.split('.', 2) as [string, string | undefined]
  const value = obj[head]
  if (tail === undefined) return value
  return isRecord(value) ? value[tail] : undefined
}

function textOf(value: unknown, kind: FieldKind): string {
  if (value === undefined || value === null) return ''
  switch (kind) {
    case 'list':
      return Array.isArray(value) ? value.map(String).join(', ') : ''
    case 'json':
      return JSON.stringify(value, null, 2)
    case 'bool':
      return value === true ? 'true' : value === false ? 'false' : ''
    default:
      return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  }
}

function fieldsFrom(base: unknown, specs: readonly FieldSpec[]): DraftFields {
  const obj = isRecord(base) ? base : {}
  const text: Record<string, string> = {}
  for (const spec of specs) {
    const value = textOf(getPath(obj, spec.path), spec.kind)
    if (value !== '') text[spec.path] = value
  }
  return { base: obj, text }
}

const MODULE_STEP_SPECS = (section: StepSection) => [
  ...moduleStepRequired(section),
  ...MODULE_STEP_OPTIONAL,
]
const STAINING_STEP_SPECS = [...STAINING_STEP_REQUIRED, ...STAINING_STEP_OPTIONAL]

function stepsFrom(list: unknown, specs: readonly FieldSpec[]): DraftStep[] {
  return (Array.isArray(list) ? list : []).map((step) => ({
    key: newStepKey(),
    ...fieldsFrom(step, specs),
  }))
}

/**
 * 받은 본문 → 초안. 「출발 틀 불러오기」가 쓴다.
 * ⚠ 부르는 쪽이 **은퇴 이름을 먼저 새 이름으로** 간다(`withCurrentVocabulary`) — 여기는 옮기기만 한다.
 */
export function draftFromPayload(payload: RecipePayload): RecipeDraft {
  const body = payload as unknown as Record<string, unknown>
  const cent = isRecord(body['centrifuge']) ? body['centrifuge'] : {}
  const asm = isRecord(body['assembler']) ? body['assembler'] : {}
  const stain = isRecord(body['staining']) ? body['staining'] : {}

  const json = emptyDraft().json
  for (const { key } of JSON_SECTIONS) {
    if (body[key] !== undefined) json[key] = JSON.stringify(body[key], null, 2)
  }

  const handled = new Set<string>([
    'centrifuge',
    'assembler',
    'staining',
    'constraints',
    // 서버 권위 메타 — 본문에 있어도 서버가 덮는다(`recipe_validate.compose`). 초안에 싣지 않는다
    'schema_version',
    ...JSON_SECTIONS.map((s) => s.key),
  ])
  const rest = Object.fromEntries(Object.entries(body).filter(([key]) => !handled.has(key)))

  return {
    centrifuge: {
      ...fieldsFrom(cent, [...CENTRIFUGE_REQUIRED, ...CENTRIFUGE_OPTIONAL]),
      steps: stepsFrom(cent['steps'], MODULE_STEP_SPECS('centrifuge')),
    },
    assembler: {
      ...fieldsFrom(asm, [...ASSEMBLER_REQUIRED, ...ASSEMBLER_OPTIONAL]),
      steps: stepsFrom(asm['steps'], MODULE_STEP_SPECS('assembler')),
    },
    staining: {
      ...fieldsFrom(stain, STAINING_OPTIONAL),
      steps: stepsFrom(stain['steps'], STAINING_STEP_SPECS),
      moduleSteps: stepsFrom(stain['module_steps'], MODULE_STEP_SPECS('staining')),
    },
    constraints: fieldsFrom(body['constraints'], CONSTRAINT_FIELDS),
    json,
    rest,
  }
}

// ── 짓기 ───────────────────────────────────────────────────────────────────

/** 편집기의 묶음(탭) — 절 셋 + 그 밖(제약 · JSON 절) */
export type EditorGroup = 'centrifuge' | 'assembler' | 'staining' | 'other'

/**
 * 고칠 것이 «화면의 어디»인가 — 편집기가 안 연 탭 · 접힌 스텝 · 접힌 선택 칸에 표시를 달 때 쓴다(2026-09-30).
 * 🔴 **표지일 뿐이다.** 본문 · 판정 · 건수는 이것과 무관하다(`message` · `where` 는 종전 그대로).
 */
export interface IssueAt {
  group: EditorGroup
  /** 스텝 안의 문제면 그 스텝의 화면 key(`DraftStep.key`) */
  stepKey?: string
  /** 칸 경로(`FieldSpec.path`) · JSON 절 키 · 목록 자체(`steps`) */
  path?: string
}

/** 제출 검증과 편집기에서 같은 칸을 가리키는 키 */
export const recipeIssueKey = (at: IssueAt) => `${at.group}:${at.stepKey ?? ''}:${at.path ?? ''}`

export interface DraftIssue {
  /** 사람이 읽는 자리 — 「염색 · 스텝 3 · 부피 (µL)」 */
  where: string
  message: string
  at: IssueAt
}

interface Parsed {
  ok: boolean
  value?: unknown
  message?: string
}

function parseField(spec: FieldSpec, raw: string, where: string): Parsed {
  const text = raw.trim()
  switch (spec.kind) {
    case 'int':
    case 'number': {
      const n = Number(text)
      if (!Number.isFinite(n))
        return { ok: false, message: t('recipeEdit.issue.number', { where }) }
      if (spec.kind === 'int' && !Number.isInteger(n)) {
        return { ok: false, message: t('recipeEdit.issue.integer', { where }) }
      }
      if (spec.min !== undefined && n < spec.min) {
        return { ok: false, message: t('recipeEdit.issue.min', { where, min: spec.min }) }
      }
      if (spec.max !== undefined && n > spec.max) {
        return { ok: false, message: t('recipeEdit.issue.max', { where, max: spec.max }) }
      }
      return { ok: true, value: n }
    }
    case 'enum': {
      if (spec.options?.includes(text) === true) return { ok: true, value: text }
      const renamed = RETIRED_SEQUENCE_NAMES[text]
      return {
        ok: false,
        message:
          spec.path === 'sequence' && renamed !== undefined
            ? t('recipeEdit.issue.retired', { where, to: renamed })
            : t('recipeEdit.issue.option', { where }),
      }
    }
    case 'bool':
      return { ok: true, value: text === 'true' }
    case 'list': {
      const items = text
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v !== '')
      if (spec.unique === true && new Set(items).size !== items.length) {
        return { ok: false, message: t('recipeEdit.issue.duplicate', { where }) }
      }
      return { ok: true, value: items }
    }
    case 'json': {
      let value: unknown
      try {
        value = JSON.parse(text)
      } catch {
        return { ok: false, message: t('recipeEdit.issue.json', { where }) }
      }
      if (spec.shape === 'object' && !isRecord(value)) {
        return { ok: false, message: t('recipeEdit.issue.shapeObject', { where }) }
      }
      if (spec.shape === 'array' && !Array.isArray(value)) {
        return { ok: false, message: t('recipeEdit.issue.shapeArray', { where }) }
      }
      return { ok: true, value }
    }
    default:
      return { ok: true, value: text }
  }
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown) {
  const [head, tail] = path.split('.', 2) as [string, string | undefined]
  if (tail === undefined) {
    if (value === undefined) delete obj[head]
    else obj[head] = value
    return
  }
  const current = obj[head]
  // 🔴 지울 것이 없으면 손대지 않는다 — 받은 빈 객체(`z_stage: {}`)를 폼이 걷어 가지 않게
  if (value === undefined && !(isRecord(current) && tail in current)) return
  const inner = isRecord(current) ? { ...current } : {}
  if (value === undefined) delete inner[tail]
  else inner[tail] = value
  if (Object.keys(inner).length === 0) delete obj[head]
  else obj[head] = inner
}

interface BuildRules {
  /** 이 칸이 필수인가(스키마 `required` · 조건부 `required`) */
  required: ReadonlySet<string>
  /** 이 칸을 본문에서 빼야 하는가(스키마 `not required`) */
  forbidden?: ReadonlySet<string>
}

/** 칸 묶음 하나를 짓는다 — `base` 복사본 위에 폼 칸만 다시 얹는다 */
function buildFields(
  fields: DraftFields,
  specs: readonly FieldSpec[],
  rules: BuildRules,
  whereOf: (spec: FieldSpec) => string,
  issues: DraftIssue[],
  scope: Omit<IssueAt, 'path'>,
): Record<string, unknown> {
  const out: Record<string, unknown> = structuredClone(fields.base)
  for (const spec of specs) {
    const where = whereOf(spec)
    const at: IssueAt = { ...scope, path: spec.path }
    if (rules.forbidden?.has(spec.path) === true) {
      setPath(out, spec.path, undefined)
      continue
    }
    const raw = fields.text[spec.path] ?? ''
    // 🔴 비운 칸은 «지운다» — 건너뛰면 불러온 옛 값이 되살아나 나간다
    //    ⚠ 받은 값이 `null`(스키마가 허락한 「미설정」 · `max_allowed_delay_sec`)이면 그대로 둔다 — 같은 뜻이다
    if (raw.trim() === '') {
      if (getPath(fields.base, spec.path) !== null) setPath(out, spec.path, undefined)
      if (rules.required.has(spec.path)) {
        issues.push({ where, message: t('recipeEdit.issue.required', { where }), at })
      }
      continue
    }
    const parsed = parseField(spec, raw, where)
    if (!parsed.ok) {
      issues.push({ where, message: parsed.message ?? '', at })
      continue
    }
    if (spec.kind === 'list' && Array.isArray(parsed.value) && parsed.value.length === 0) {
      setPath(out, spec.path, undefined)
      if (rules.required.has(spec.path)) {
        issues.push({ where, message: t('recipeEdit.issue.required', { where }), at })
      }
      continue
    }
    setPath(out, spec.path, parsed.value)
  }
  return out
}

const pathsOf = (specs: readonly FieldSpec[]) => new Set(specs.map((s) => s.path))

function label(spec: FieldSpec): string {
  return t(spec.label)
}

function buildModuleSteps(
  steps: readonly DraftStep[],
  section: StepSection,
  sectionLabel: string,
  issues: DraftIssue[],
): Record<string, unknown>[] {
  const required = moduleStepRequired(section)
  const specs = [...required, ...MODULE_STEP_OPTIONAL]
  return steps.map((step, index) => {
    const prefix = `${sectionLabel} · ${t('recipeEdit.stepN', { n: index + 1 })}`
    const built = buildFields(
      step,
      specs,
      { required: pathsOf(required) },
      (spec) => `${prefix} · ${label(spec)}`,
      issues,
      { group: section, stepKey: step.key },
    )
    // 🔴 `ModuleLocation.anyOf` — 자리를 적었으면 종류나 지점 중 하나는 있어야 한다
    for (const side of ['source', 'target'] as const) {
      const loc = built[side]
      if (isRecord(loc) && loc['location_kind'] === undefined && loc['point_id'] === undefined) {
        const where = `${prefix} · ${t(side === 'source' ? 'recipeEdit.f.source' : 'recipeEdit.f.target')}`
        issues.push({
          where,
          message: t('recipeEdit.issue.location', { where }),
          // 자리 칸 넷은 전부 선택 칸이다 — 그 묶음에 표시가 서도록 종류 칸의 경로를 가리킨다
          at: { group: section, stepKey: step.key, path: `${side}.location_kind` },
        })
      }
    }
    return built
  })
}

function buildStainingSteps(
  steps: readonly DraftStep[],
  sectionLabel: string,
  issues: DraftIssue[],
): Record<string, unknown>[] {
  return steps.map((step, index) => {
    const prefix = `${sectionLabel} · ${t('recipeEdit.stepN', { n: index + 1 })}`
    const operation = (step.text['operation'] ?? '').trim()
    const toWaste = (step.text['to_waste'] ?? '').trim()
    const required = new Set([
      ...STAINING_STEP_REQUIRED.map((s) => s.path),
      ...stainingConditionalRequired(operation, toWaste),
    ])
    return buildFields(
      step,
      STAINING_STEP_SPECS,
      { required, forbidden: new Set(stainingForbidden(operation)) },
      (spec) => `${prefix} · ${label(spec)}`,
      issues,
      { group: 'staining', stepKey: step.key },
    )
  })
}

export interface BuiltRecipe {
  /** `POST /recipes` 의 `payload` 그대로 */
  payload: RecipePayload
  /** 비어 있어야 「만들기」가 열린다 */
  issues: DraftIssue[]
}

/**
 * 초안 → 서버가 받는 본문.
 * 🔴 **키 순서·모양을 스키마대로 짓는다.** 절 셋은 필수라 늘 싣고, 나머지는 값이 있을 때만 싣는다.
 * ⚠ `schema_version` 을 본문에 넣지 않는다 — 봉투(`RecipeWriteEnvelope.schema_version`)가 실어 간다.
 */
export function buildRecipePayload(draft: RecipeDraft): BuiltRecipe {
  const issues: DraftIssue[] = []
  const centLabel = t('section.centrifuge')
  const asmLabel = t('section.assembler')
  const stainLabel = t('section.staining')

  const centrifuge = buildFields(
    draft.centrifuge,
    [...CENTRIFUGE_REQUIRED, ...CENTRIFUGE_OPTIONAL],
    { required: pathsOf(CENTRIFUGE_REQUIRED) },
    (spec) => `${centLabel} · ${label(spec)}`,
    issues,
    { group: 'centrifuge' },
  )
  const centSteps = buildModuleSteps(draft.centrifuge.steps, 'centrifuge', centLabel, issues)
  // `steps` 는 선택이지만 있으면 `minItems: 1` 이다 — 비었으면 싣지 않는다
  if (centSteps.length > 0) centrifuge['steps'] = centSteps
  else delete centrifuge['steps']

  const assembler = buildFields(
    draft.assembler,
    [...ASSEMBLER_REQUIRED, ...ASSEMBLER_OPTIONAL],
    { required: pathsOf(ASSEMBLER_REQUIRED) },
    (spec) => `${asmLabel} · ${label(spec)}`,
    issues,
    { group: 'assembler' },
  )
  const asmSteps = buildModuleSteps(draft.assembler.steps, 'assembler', asmLabel, issues)
  if (asmSteps.length > 0) assembler['steps'] = asmSteps
  else delete assembler['steps']

  const staining = buildFields(
    draft.staining,
    STAINING_OPTIONAL,
    { required: new Set() },
    (spec) => `${stainLabel} · ${label(spec)}`,
    issues,
    { group: 'staining' },
  )
  // 🔴 `StainingParams.steps` 는 필수이고 `minItems: 1` 이다
  if (draft.staining.steps.length === 0) {
    const where = `${stainLabel} · ${t('recipeEdit.steps')}`
    issues.push({
      where,
      message: t('recipeEdit.issue.noSteps', { where }),
      at: { group: 'staining', path: 'steps' },
    })
  }
  staining['steps'] = buildStainingSteps(draft.staining.steps, stainLabel, issues)
  const moduleSteps = buildModuleSteps(
    draft.staining.moduleSteps,
    'staining',
    `${stainLabel} · ${t('recipeEdit.moduleSteps')}`,
    issues,
  )
  if (moduleSteps.length > 0) staining['module_steps'] = moduleSteps
  else delete staining['module_steps']

  const payload: Record<string, unknown> = { centrifuge, assembler, staining }

  for (const section of JSON_SECTIONS) {
    const raw = draft.json[section.key]
    if (raw.trim() === '') continue
    const where = t(section.label)
    const parsed = parseField(
      { path: section.key, label: section.label, kind: 'json', shape: section.shape },
      raw,
      where,
    )
    if (parsed.ok) payload[section.key] = parsed.value
    else
      issues.push({
        where,
        message: parsed.message ?? '',
        at: { group: 'other', path: section.key },
      })
  }

  const constraints = buildFields(
    draft.constraints,
    CONSTRAINT_FIELDS,
    { required: new Set() },
    (spec) => label(spec),
    issues,
    { group: 'other' },
  )
  if (Object.keys(constraints).length > 0) payload['constraints'] = constraints

  return { payload: { ...draft.rest, ...payload } as unknown as RecipePayload, issues }
}

/**
 * 폼이 모르는 키가 몇 곳인가 — **스키마에 없는 칸**(`additionalProperties: false`)이라 서버가 거부한다.
 * 🔴 **세기만 한다. 지우지 않는다.** 불러온 옛 본문에만 생기고(v0.9.1 에서 스키마가 조여졌다 —
 *    `binds`·`verify_grip`·`verify_released`·`collaborative_6axis`), 그 뜻을 옮기는 일은 백엔드 몫이다.
 */
export function unknownKeyCount(draft: RecipeDraft): number {
  const moduleKnown = knownKeys([...moduleStepRequired('centrifuge'), ...MODULE_STEP_OPTIONAL])
  const stainKnown = knownKeys(STAINING_STEP_SPECS)
  const count = (fields: DraftFields, known: Set<string>) =>
    Object.keys(fields.base).filter((key) => !known.has(key)).length
  const sectionKnown = (specs: readonly FieldSpec[], lists: string[]) =>
    new Set([...knownKeys(specs), ...lists])

  let n = Object.keys(draft.rest).length
  n += count(
    draft.centrifuge,
    sectionKnown([...CENTRIFUGE_REQUIRED, ...CENTRIFUGE_OPTIONAL], ['steps']),
  )
  n += count(
    draft.assembler,
    sectionKnown([...ASSEMBLER_REQUIRED, ...ASSEMBLER_OPTIONAL], ['steps']),
  )
  n += count(draft.staining, sectionKnown(STAINING_OPTIONAL, ['steps', 'module_steps']))
  n += count(draft.constraints, knownKeys(CONSTRAINT_FIELDS))
  for (const step of [
    ...draft.centrifuge.steps,
    ...draft.assembler.steps,
    ...draft.staining.moduleSteps,
  ]) {
    n += count(step, moduleKnown)
  }
  for (const step of draft.staining.steps) n += count(step, stainKnown)
  return n
}

/** 스텝 목록 편집 — 순수 함수. 화면이 상태를 직접 주무르지 않게 한 곳에 둔다 */
export function moveStep<T>(list: readonly T[], index: number, delta: -1 | 1): T[] {
  const to = index + delta
  if (to < 0 || to >= list.length) return [...list]
  const next = [...list]
  const [item] = next.splice(index, 1)
  if (item !== undefined) next.splice(to, 0, item)
  return next
}
